import axios from "axios";
import { API_BASE_URL } from "../config";
import supabase from "../config/supabaseConfig";

// Helper to save auth session
const saveAuthSession = (authData) => {

  if (!authData.access_token) {
    console.error("No access token in auth data");
    return;
  }

  try {
    localStorage.setItem("token", authData.access_token);
    localStorage.setItem("refresh_token", authData.refresh_token);

    // Handle the specific user structure from the response
    const user = {
      id: authData.user.id,
      email: authData.user.email,
      email_confirmed_at: authData.user.email_confirmed_at,
      last_sign_in_at: authData.user.last_sign_in_at,
    };

    localStorage.setItem("user", JSON.stringify(user));
  } catch (error) {
   
  }
};

// Helper to get auth tokens
const getAuthTokens = () => {
  try {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refresh_token");
    return { token, refreshToken };
  } catch (error) {
    return { token: null, refreshToken: null };
  }
};

// Helper to clear auth session
const clearAuthSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
};

// Helper to update stored user info
const updateStoredUserInfo = (userData) => {
  try {
    localStorage.setItem("user", JSON.stringify(userData));
    return true;
  } catch (error) {
    console.error("Error updating stored user info:", error);
    return false;
  }
};

export const authApi = {
  // Login endpoint
  login: async (credentials) => {
    try {
      // Use Supabase for authentication
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });
      
      if (error) throw error;
      
      // Create auth data object from Supabase response
      const response = {
        data: {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
          user: data.user
        }
      };

      // Save auth session data
      saveAuthSession(response.data);

      return response.data;
    } catch (error) {
      console.error("Error during login:", error);
      throw error;
    }
  },

  // Register endpoint
  register: async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, {
        full_name: userData.fullName,
        email: userData.email,
        password: userData.password,
        company_name: userData.companyName,
      });

      // Save auth session data if registration returns tokens
      if (response.data.access_token) {
        saveAuthSession(response.data);
      }
      return response.data;
    } catch (error) {
      console.error("Error during registration:", error);
      throw error;
    }
  },

  // Logout helper
  logout: () => {
    clearAuthSession();
  },

  // Get current auth state
  getAuthState: () => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return {
      isAuthenticated: !!token,
      user: user ? JSON.parse(user) : null,
      token,
    };
  },

  // NEW METHOD: Get user details from the server
  getUserDetails: async () => {
    try {
      // Get the auth token
      const { token } = getAuthTokens();

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Make the API request with the auth token
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Update the stored user information with the fresh data
      updateStoredUserInfo(response.data);

      return response.data;
    } catch (error) {
      console.error("Error fetching user details:", error);

      // Check if it's an auth error (401)
      if (error.response && error.response.status === 401) {
        // Clear session as the token might be expired or invalid
        clearAuthSession();
      }

      throw error;
    }
  },

  // Refresh the user's data in local storage
  refreshUserData: async () => {
    try {
      const userData = await authApi.getUserDetails();
      return userData;
    } catch (error) {
      throw error;
    }
  },
  // Update user profile
  updateProfile: async (profileData) => {
    try {
      // Get the auth token
      const { token } = getAuthTokens();

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Make the API request to update profile
      const response = await axios.put(
        `${API_BASE_URL}/auth/profile`,
        {
          company_name: profileData.company_name,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update the stored user information with the fresh data
      updateStoredUserInfo(response.data);

      return response.data;
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  },

  // Change password
  changePassword: async (passwordData) => {
    try {
      // Get the auth token
      const { token } = getAuthTokens();

      if (!token) {
        throw new Error("No authentication token available");
      }

      // Make the API request to change password
      const response = await axios.post(
        `${API_BASE_URL}/auth/change-password`,
        {
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error changing password:", error);
      throw error;
    }
  },
};

export default authApi;
